import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { Box, Button, Card, CardContent, Chip, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

import type { ToolCallMessagePartProps } from '@assistant-ui/react';

// XSS-safe sanitize schema — consistent with message-row.tsx
const sanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    code: [...(defaultSchema.attributes?.code ?? []), ['className', /^language-/]],
  },
};

function parseContent(result: unknown): string {
  if (typeof result === 'string') return result;
  if (result && typeof result === 'object') {
    const obj = result as Record<string, unknown>;
    if (typeof obj.content === 'string') return obj.content;
    if (typeof obj.text === 'string') return obj.text;
    return JSON.stringify(result, null, 2);
  }
  // eslint-disable-next-line @typescript-eslint/no-base-to-string
  return String(result ?? '');
}

export const KnowledgeTool = ({ toolName, argsText, result, status }: ToolCallMessagePartProps) => {
  const { t } = useTranslation();

  if (status.type === 'running') {
    return (
      <Box display="flex" alignItems="center" gap={1} py={0.5}>
        <CircularProgress size={16} />
        <Typography variant="body2" fontStyle="italic">
          {t('aiChat.tool.running', { action: t(`aiChat.tool.name.${toolName}`, toolName) })}
        </Typography>
      </Box>
    );
  }

  if (status.type === 'requires-action') {
    return (
      <Card elevation={1} sx={{ borderRadius: 2, p: 1 }}>
        <CardContent>
          <Typography variant="body2" mb={1}>
            {t(`aiChat.tool.name.${toolName}`, toolName)}
          </Typography>
          <Box display="flex" gap={1}>
            <Button variant="contained" size="small" autoFocus>
              {t('aiChat.tool.confirm')}
            </Button>
            <Button variant="text" size="small">
              {t('aiChat.tool.cancel')}
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (status.type === 'incomplete') {
    return (
      <Box>
        <Chip
          icon={<ErrorOutlineIcon />}
          label={t('aiChat.tool.failed', { toolName: t(`aiChat.tool.name.${toolName}`, toolName) })}
          color="error"
          size="small"
          variant="outlined"
        />
        {argsText && (
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            {argsText}
          </Typography>
        )}
      </Box>
    );
  }

  // Complete state: render knowledge block with markdown
  const content = parseContent(result);

  return (
    <Box
      sx={{
        bgcolor: 'grey.50',
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        p: 1.5,
      }}
    >
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <MenuBookIcon fontSize="small" color="action" />
        <Typography variant="caption" color="text.secondary">
          {t(`aiChat.tool.name.${toolName}`, toolName)}
        </Typography>
      </Box>
      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[[rehypeSanitize, sanitizeSchema]]}>
        {content}
      </ReactMarkdown>
    </Box>
  );
};
